/**
 * Layout model helpers for the pages system.
 *
 * Pages are vertical document flow. A layout node is a CSS Grid of logical
 * tracks. Children store placement metadata — the renderer invents wrappers.
 */

import {
  DEFAULT_TRACKS,
  HEIGHT_POLICIES,
  LAYOUT_BREAKPOINTS,
} from './pageBuilderContract';

const BREAKPOINT_FALLBACK = {
  mobile: ['mobile'],
  tablet: ['tablet', 'desktop', 'mobile'],
  desktop: ['desktop', 'tablet', 'mobile'],
};

export function clampInt(value, min, max, fallback) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, Math.round(num)));
}

export function normalizeTracks(tracks) {
  if (tracks == null) {
    return {
      mobile: DEFAULT_TRACKS,
      tablet: DEFAULT_TRACKS,
      desktop: DEFAULT_TRACKS,
    };
  }

  if (typeof tracks === 'number' || typeof tracks === 'string') {
    const n = clampInt(tracks, 1, DEFAULT_TRACKS, DEFAULT_TRACKS);
    return { mobile: n, tablet: n, desktop: n };
  }

  return {
    mobile: clampInt(tracks.mobile, 1, DEFAULT_TRACKS, DEFAULT_TRACKS),
    tablet: clampInt(tracks.tablet, 1, DEFAULT_TRACKS, DEFAULT_TRACKS),
    desktop: clampInt(tracks.desktop, 1, DEFAULT_TRACKS, DEFAULT_TRACKS),
  };
}

export const BLOCK_SPANS = [3, 4, 6, 8, 12];

/**
 * Designed spans that fit this parent. Always includes the parent's
 * full width so a 1-track or 5-track lane still has a valid choice.
 */
export function blockSpansForTracks(tracks = DEFAULT_TRACKS) {
  const n = clampInt(tracks, 1, DEFAULT_TRACKS, DEFAULT_TRACKS);
  const spans = BLOCK_SPANS.filter((span) => span <= n);
  if (!spans.includes(n)) spans.push(n);
  return spans;
}

/**
 * Pixel width of one track in a CSS Grid of `tracks` columns.
 */
export function trackSizePx(containerWidth, tracks = DEFAULT_TRACKS, gap = 16) {
  const n = clampInt(tracks, 1, DEFAULT_TRACKS, DEFAULT_TRACKS);
  const width = Number(containerWidth);
  const gutter = Number(gap);
  if (!Number.isFinite(width) || width <= 0) return 0;
  const gapPx = Number.isFinite(gutter) ? Math.max(0, gutter) : 16;
  return (width - gapPx * (n - 1)) / n;
}

/**
 * Pixel width of an N-track item, including the gaps between those tracks.
 */
export function spanWidthPx(span, trackPx, gap = 16) {
  const n = Number(span);
  const track = Number(trackPx);
  const gutter = Number(gap);
  if (!Number.isFinite(n) || n <= 0 || !Number.isFinite(track) || track <= 0) {
    return 0;
  }
  const gapPx = Number.isFinite(gutter) ? Math.max(0, gutter) : 16;
  return n * track + Math.max(0, n - 1) * gapPx;
}

/**
 * Walk past display:contents (zero-box) ancestors to the grid that
 * actually has a width. Journey view keeps `.page-node-children` as
 * contents; edit mode makes that node the grid.
 */
export function layoutContainerWidth(start) {
  let el = start;
  while (el) {
    const width = Number(el.clientWidth);
    if (Number.isFinite(width) && width > 0) return width;
    el = el.parentElement;
  }
  return 0;
}

/**
 * Snap a dragged pixel width onto a designed span for this parent.
 * Compares against N*track + (N-1)*gap, not width/track.
 */
export function snapBlockSpan(
  widthPx,
  trackPx,
  allowed = BLOCK_SPANS,
  gap = 16
) {
  const spans =
    Array.isArray(allowed) && allowed.length ? allowed : BLOCK_SPANS;
  if (!Number.isFinite(widthPx) || widthPx <= 0) {
    return spans[0];
  }
  if (!Number.isFinite(trackPx) || trackPx <= 0) {
    return spans[0];
  }
  return spans.reduce((best, span) => {
    const designed = spanWidthPx(span, trackPx, gap);
    const bestDesigned = spanWidthPx(best, trackPx, gap);
    const dist = Math.abs(widthPx - designed);
    const bestDist = Math.abs(widthPx - bestDesigned);
    if (dist < bestDist) return span;
    if (dist === bestDist && span > best) return span;
    return best;
  }, spans[0]);
}

/**
 * Set the designed desktop width. Column resets to 1 so the chosen span
 * always fits the parent track count. Tablet uses at least 6 only when
 * the parent has 6+ tracks; mobile stays full width of the parent.
 */
export function setBlockSpan(
  layout,
  span,
  tracksByBreakpoint = DEFAULT_TRACKS
) {
  const tracks = normalizeTracks(tracksByBreakpoint);
  const allowed = blockSpansForTracks(tracks.desktop);
  const current = layout && typeof layout === 'object' ? layout : {};
  const nextSpan = allowed.includes(span)
    ? span
    : clampInt(span, 1, tracks.desktop, tracks.desktop);
  const order = clampInt(
    current.desktop?.order ?? current.tablet?.order ?? current.mobile?.order,
    -999,
    999,
    0
  );
  const tabletSpan =
    tracks.tablet >= 6
      ? Math.min(tracks.tablet, Math.max(nextSpan, 6))
      : Math.min(tracks.tablet, nextSpan);

  return {
    desktop: { column: 1, span: nextSpan, order },
    tablet: { column: 1, span: tabletSpan, order },
    mobile: { column: 1, span: tracks.mobile, order },
  };
}

export function packLayoutColumns(
  siblings,
  breakpoint,
  tracks = DEFAULT_TRACKS
) {
  const ordered = siblings
    .map((node, index) => ({
      id: node.id,
      index,
      placement: resolvePlacement(node, breakpoint, tracks),
    }))
    .sort((a, b) => a.placement.order - b.placement.order || a.index - b.index);
  const rows = [];

  return ordered.map(({ id, placement }) => {
    const { span } = placement;
    const availableColumn = (used) => {
      for (let offset = 0; offset < tracks; offset += 1) {
        const column = ((placement.column + offset - 1) % tracks) + 1;
        if (column + span - 1 > tracks) continue;
        if (used.slice(column - 1, column + span - 1).every((cell) => !cell)) {
          return column;
        }
      }
      return null;
    };
    const item = {
      id,
      previousColumn: placement.column,
      placement: { ...placement },
    };
    let row = rows.find(
      (candidate) => availableColumn(candidate.used) !== null
    );
    if (!row) {
      row = rows.find((candidate) => {
        const occupied = candidate.items.reduce(
          (total, entry) => total + entry.placement.span,
          0
        );
        return occupied + span <= tracks;
      });
      if (row) {
        row.items.push(item);
        let column = 1;
        row.items.forEach((entry) => {
          entry.placement.column = column;
          column += entry.placement.span;
        });
        row.used.fill(false);
        row.used.fill(true, 0, column - 1);
        return item;
      }
      row = { used: Array(tracks).fill(false), items: [] };
      rows.push(row);
    }
    const column =
      row.items.length === 0 && rows.length > 1 ? 1 : availableColumn(row.used);
    item.placement.column = column;
    row.used.fill(true, column - 1, column + span - 1);
    row.items.push(item);
    return item;
  });
}

export function normalizePlacement(placement, tracks = DEFAULT_TRACKS) {
  const column = clampInt(placement?.column, 1, tracks, 1);
  const maxSpan = tracks - column + 1;
  const span = clampInt(placement?.span, 1, maxSpan, tracks);
  const order = clampInt(placement?.order, -999, 999, 0);
  return { column, span, order };
}

export function resolvePlacement(node, breakpoint, tracks = DEFAULT_TRACKS) {
  const layout =
    node?.layout && typeof node.layout === 'object' ? node.layout : {};
  const keys = BREAKPOINT_FALLBACK[breakpoint] || BREAKPOINT_FALLBACK.desktop;

  let found = null;
  keys.forEach((key) => {
    if (!found && layout[key] && typeof layout[key] === 'object') {
      found = layout[key];
    }
  });

  if (!found && breakpoint === 'mobile') {
    // Mobile stacks full width but keeps the order of a wider placement.
    const wider = ['tablet', 'desktop']
      .map((key) => layout[key])
      .find((placement) => placement && typeof placement === 'object');
    return normalizePlacement(
      { column: 1, span: tracks, order: wider?.order ?? 0 },
      tracks
    );
  }

  return normalizePlacement(found, tracks);
}

export function placementCssVars(node, tracksByBreakpoint) {
  const tracks = normalizeTracks(tracksByBreakpoint);
  const vars = {};

  LAYOUT_BREAKPOINTS.forEach((breakpoint) => {
    const placement = resolvePlacement(node, breakpoint, tracks[breakpoint]);
    vars[`--layout-col-${breakpoint}`] = String(placement.column);
    vars[`--layout-span-${breakpoint}`] = String(placement.span);
    vars[`--layout-order-${breakpoint}`] = String(placement.order);
  });

  return vars;
}

export function layoutTrackCssVars(layoutProps = {}) {
  const tracks = normalizeTracks(layoutProps.tracks);
  const gap =
    typeof layoutProps.gap === 'number'
      ? `${layoutProps.gap}px`
      : layoutProps.gap || '16px';

  return {
    '--layout-tracks-mobile': String(tracks.mobile),
    '--layout-tracks-tablet': String(tracks.tablet),
    '--layout-tracks-desktop': String(tracks.desktop),
    '--layout-gap': gap,
  };
}

export function normalizeHeight(height) {
  if (!height || typeof height !== 'object') {
    return { policy: 'auto' };
  }

  const policy = HEIGHT_POLICIES.includes(height.policy)
    ? height.policy
    : 'auto';
  const normalized = { policy };

  if (height.max != null) {
    normalized.max = Number(height.max);
  }
  if (height.value != null) {
    normalized.value = Number(height.value);
  }
  if (typeof height.aspectRatio === 'string' && height.aspectRatio) {
    normalized.aspectRatio = height.aspectRatio;
  }

  return normalized;
}

export function heightCssVars(height) {
  const normalized = normalizeHeight(height);
  const vars = {};

  if (normalized.max != null && Number.isFinite(normalized.max)) {
    vars['--block-height-max'] = `${normalized.max}px`;
  }
  if (normalized.value != null && Number.isFinite(normalized.value)) {
    vars['--block-height'] = `${normalized.value}px`;
  }
  if (normalized.aspectRatio) {
    vars['--block-aspect-ratio'] = normalized.aspectRatio;
  }

  return vars;
}

export function heightClass(height) {
  const normalized = normalizeHeight(height);
  const classes = [`page-node-wrapper--height-${normalized.policy}`];
  if (normalized.aspectRatio) {
    classes.push('page-node-wrapper--aspect');
  }
  return classes;
}

function gridSpanFromWidth(value, fallback = DEFAULT_TRACKS) {
  if (value == null || value === '') return fallback;
  if (value === 'grow' || value === 'auto') return fallback;
  if (typeof value === 'string' && value.includes('%')) {
    const pct = parseFloat(value);
    if (!Number.isFinite(pct)) return fallback;
    return clampInt(
      Math.round((pct / 100) * DEFAULT_TRACKS),
      1,
      DEFAULT_TRACKS,
      fallback
    );
  }
  return clampInt(value, 1, DEFAULT_TRACKS, fallback);
}

/**
 * Old Vuetify col widths: cols/xs from 0, sm from 600, md from 960, lg from 1280.
 * New layout breaks: mobile <600, tablet >=600, desktop >=960.
 */
function colSpansFromProps(props = {}) {
  const xs = props.cols ?? props.xs;
  return {
    mobile: gridSpanFromWidth(xs, DEFAULT_TRACKS),
    tablet: gridSpanFromWidth(props.sm ?? xs, DEFAULT_TRACKS),
    desktop: gridSpanFromWidth(
      props.md ?? props.lg ?? props.sm ?? xs,
      DEFAULT_TRACKS
    ),
  };
}

function takeTrack(cursor, span, tracks = DEFAULT_TRACKS) {
  const fit = clampInt(span, 1, tracks, tracks);
  const column = cursor + fit - 1 > tracks ? 1 : cursor;
  const next = column + fit;
  return {
    column,
    span: fit,
    next: next > tracks ? 1 : next,
  };
}

function childrenOf(nodes, parentId, stored, rootKey) {
  const ids = orderChildIds(nodes, parentId, stored, rootKey);
  return ids.map((id) => nodes[id]).filter(Boolean);
}

function hasAfterIdChain(nodes) {
  const ids = new Set(nodes.map((node) => node.id));
  return nodes.some((node) => node.afterId && ids.has(node.afterId));
}

function orderChildIds(nodes, parentId, stored, rootKey) {
  const kids = Object.values(nodes).filter(
    (node) => (node.parentId || null) === (parentId || null)
  );
  if (hasAfterIdChain(kids)) {
    return sortByAfterId(kids).map((child) => child.id);
  }
  const storedKey = parentId || rootKey;
  const listed = Array.isArray(stored?.[storedKey]) ? stored[storedKey] : [];
  if (listed.length) {
    const kidIds = new Set(kids.map((child) => child.id));
    const kept = listed.filter((id) => kidIds.has(id));
    const extras = kids
      .map((child) => child.id)
      .filter((id) => !listed.includes(id));
    return [...kept, ...extras];
  }
  return kids.map((child) => child.id);
}

function sortByAfterId(nodes) {
  if (nodes.length < 2) return nodes;
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const nextByPrev = new Map();
  const mentioned = new Set();
  nodes.forEach((node) => {
    if (node.afterId && byId.has(node.afterId)) {
      nextByPrev.set(node.afterId, node.id);
      mentioned.add(node.id);
    }
  });
  const ordered = [];
  const seen = new Set();
  const walk = (id) => {
    while (id && byId.has(id) && !seen.has(id)) {
      seen.add(id);
      ordered.push(byId.get(id));
      id = nextByPrev.get(id);
    }
  };
  nodes.forEach((node) => {
    if (!mentioned.has(node.id)) walk(node.id);
  });
  nodes.forEach((node) => {
    if (!seen.has(node.id)) walk(node.id);
  });
  return ordered;
}

function depthOf(nodes, nodeId, seen = new Set()) {
  if (!nodeId || seen.has(nodeId)) return 0;
  seen.add(nodeId);
  const node = nodes[nodeId];
  if (!node || !node.parentId) return 0;
  return 1 + depthOf(nodes, node.parentId, seen);
}

/**
 * Convert persisted row/col trees into layout + placement metadata.
 * Safe to run on already-migrated content.
 */
export function migrateRowColContent(pageData = {}) {
  const sourceNodes =
    pageData.nodes && typeof pageData.nodes === 'object' ? pageData.nodes : {};
  const nodes = {};

  Object.entries(sourceNodes).forEach(([id, node]) => {
    nodes[id] = {
      ...node,
      id: node.id || id,
      props: { ...(node.props || {}) },
      layout: node.layout ? { ...node.layout } : undefined,
      height: node.height ? { ...node.height } : undefined,
    };
  });

  const storedChildren =
    pageData.childrenByParent && typeof pageData.childrenByParent === 'object'
      ? pageData.childrenByParent
      : {};
  const rootKey = `root_${pageData.pageId || pageData.id || ''}`;

  const rows = Object.values(nodes)
    .filter((node) => node.nodeType === 'row')
    .sort((a, b) => depthOf(nodes, b.id) - depthOf(nodes, a.id));

  rows.forEach((row) => {
    const kids = childrenOf(nodes, row.id, storedChildren, rootKey);
    const cols = kids.filter((child) => child.nodeType === 'col');
    const otherKids = kids.filter((child) => child.nodeType !== 'col');

    row.nodeType = 'layout';
    row.droppable = true;
    row.draggable = true;
    row.props = {
      tracks: DEFAULT_TRACKS,
      gap: row.props.gap ?? 24,
      align: row.props.align || 'stretch',
      className: row.props.className || '',
    };

    const cursors = { mobile: 1, tablet: 1, desktop: 1 };
    let order = 0;
    let prevPlacedId = null;

    const placeChild = (child, placements) => {
      order += 1;
      child.parentId = row.id;
      child.afterId = prevPlacedId;
      prevPlacedId = child.id;
      child.layout = {
        desktop: { ...placements.desktop, order },
        tablet: { ...placements.tablet, order },
        mobile: { ...placements.mobile, order },
      };
    };

    cols.forEach((col) => {
      const spans = colSpansFromProps(col.props);
      const placements = {};

      LAYOUT_BREAKPOINTS.forEach((breakpoint) => {
        const taken = takeTrack(cursors[breakpoint], spans[breakpoint]);
        cursors[breakpoint] = taken.next;
        placements[breakpoint] = { column: taken.column, span: taken.span };
      });

      childrenOf(nodes, col.id, storedChildren, rootKey).forEach((child) => {
        placeChild(child, placements);
      });

      delete nodes[col.id];
    });

    otherKids.forEach((child) => {
      placeChild(child, {
        desktop: { column: 1, span: DEFAULT_TRACKS },
        tablet: { column: 1, span: DEFAULT_TRACKS },
        mobile: { column: 1, span: DEFAULT_TRACKS },
      });
    });
  });

  Object.values(nodes).forEach((node) => {
    if (node.nodeType === 'col') {
      node.nodeType = 'markdownStream';
      node.props = { chunks: [''], status: 'done', ...node.props };
    }
  });

  const childrenByParent = {};
  Object.values(nodes).forEach((node) => {
    const key = node.parentId || rootKey;
    if (!childrenByParent[key]) {
      childrenByParent[key] = orderChildIds(
        nodes,
        node.parentId || null,
        storedChildren,
        rootKey
      );
    }
  });

  return {
    ...pageData,
    pageId: pageData.pageId || pageData.id,
    nodes,
    childrenByParent,
  };
}

export function isLayoutNode(node) {
  return node?.nodeType === 'layout';
}
